import React, { createContext, useContext, useEffect, useCallback, useReducer } from "react";
import { getAllSensors } from "../services/sensorsService.js";

const SensorsMapContext = createContext(null);
const ACTIONS = {
	SET_COORDS_ATRI: "SET_COORDS_ATRI",
	SET_PINS: "SET_PINS",
	SET_SENSOR_GROUPS: "SET_SENSOR_GROUPS",
	SET_USER_LOCATION: "SET_USER_LOCATION",
	TOGGLE_ADD_PIN: "TOGGLE_ADD_PIN",
	SET_PENDING_PIN: "SET_PENDING_PIN",
	SET_PIN_TO_DELETE: "SET_PIN_TO_DELETE",
	TOGGLE_MODAL: "TOGGLE_MODAL",
	SET_SENSORS: "SET_SENSORS",
	SET_SELECTED_SENSOR: "SET_SELECTED_SENSOR",
	CLEAR_SELECTED_SENSOR: "CLEAR_SELECTED_SENSOR",
	SET_SELECTED_TYPE: "SET_SELECTED_TYPE",
	SET_PINS_GROUPS: "SET_PINS_GROUPS",
	TOGGLE_GROUP_SELECTION: "TOGGLE_GROUP_SELECTION",
	SELECT_GROUP: "SELECT_GROUP",
	SET_FILTER_MENU_OPEN: "SET_FILTER_MENU_OPEN",
	SET_SELECTED_GROUPS: "SET_SELECTED_GROUPS",
};

// Reducer per gestire lo stato
function mapReducer(state, action) {
	switch (action.type) {
		case ACTIONS.SET_COORDS_ATRI:
			return { ...state, coordsAtri: action.payload };
		case ACTIONS.SET_PINS_GROUPS:
			return { ...state, pinsGroups: action.payload };
		case ACTIONS.SET_PINS:
			return { ...state, pins: action.payload };
		case ACTIONS.SET_SENSOR_GROUPS:
			return { ...state, sensorGroups: action.payload };
		case ACTIONS.SET_USER_LOCATION:
			return { ...state, userLocation: action.payload };
		case ACTIONS.TOGGLE_ADD_PIN:
			return {
				...state,
				addPinEnabled: !state.addPinEnabled,
				pendingPin: null,
				modalOpen: false,
				selectedType: null,
				filterMenuOpen: 0,
			};
		case ACTIONS.SET_PENDING_PIN:
			return { ...state, pendingPin: action.payload, modalOpen: true };
		case ACTIONS.SET_PIN_TO_DELETE:
			return { ...state, pinToDelete: action.payload };
		case ACTIONS.TOGGLE_MODAL:
			return { ...state, modalOpen: action.payload };
		case ACTIONS.SET_SENSORS:
			return { ...state, sensors: action.payload };
		case ACTIONS.SET_SELECTED_SENSOR:
			return { ...state, selectedSensor: action.payload };
		case ACTIONS.SET_SELECTED_TYPE:
			return { ...state, selectedType: action.payload, addPinEnabled: false };
		case ACTIONS.CLEAR_SELECTED_SENSOR:
			return { ...state, selectedSensor: null };
		case ACTIONS.TOGGLE_GROUP_SELECTION:
			const updatedPinsGroup = state.pinsGroups.map((group) =>
				group.id === action.payload ? { ...group, selected: group.selected ? 0 : 1 } : group
			);
			return { ...state, pinsGroups: updatedPinsGroup };
		case ACTIONS.SELECT_GROUP:
			return {
				...state,
				pinsGroups: state.pinsGroups.map((group) =>
					group.id === action.payload ? { ...group, selected: 1 } : group
				),
			};
		case ACTIONS.SET_SELECTED_GROUPS:
			return {
				...state,
				pinsGroups: state.pinsGroups.map((group) =>
					action.payload.includes(group.id)
						? { ...group, selected: 1 }
						: { ...group, selected: 0 }
				),
			};

		case ACTIONS.SET_FILTER_MENU_OPEN:
			return { ...state, filterMenuOpen: action.payload };
		default:
			return state;
	}
}

export const SensorsMapProvider = ({ children }) => {
	const initialState = {
		coordsAtri: null,
		pins: [],
		sensorGroups: [],
		addPinEnabled: false,
		modalOpen: false,
		pendingPin: null,
		pinToDelete: null,
		userLocation: null,
		selectedType: null,
		selectedSensor: null,
		sensors: [],
		pinsGroups: [],
		filterMenuOpen: 0,
	};

	const [state, dispatch] = useReducer(mapReducer, initialState);

	const groupSensorsByPosition = (sensors) => {
		const grouped = {};

		sensors.forEach((sensor) => {
			const { latitude, longitude, name, type, value, chart_link } = sensor;
			const key = `${latitude},${longitude}`;

			if (!grouped[key]) {
				grouped[key] = {
					position: [latitude, longitude],
					sensors: [],
				};
			}

			grouped[key].sensors.push({ name, type, value, chart_link });
		});

		return Object.values(grouped);
	};

	const loadSensorsData = useCallback(async () => {
		getAllSensors().then((sensors) => {
			const grouped = groupSensorsByPosition(sensors.data.data);
			dispatch({ type: ACTIONS.SET_SENSORS, payload: grouped });
		});
	}, []);

	useEffect(() => {
		loadSensorsData();
	}, [loadSensorsData]);

	return (
		<SensorsMapContext.Provider
			value={{
				state,
				dispatch,
				ACTIONS,
				reloadSensors: loadSensorsData,
			}}
		>
			{children}
		</SensorsMapContext.Provider>
	);
};

export const useSensorsMap = () => {
	const context = useContext(SensorsMapContext);
	if (!context) {
		throw new Error("useSensorsMap deve essere usato dentro SensorsMapProvider");
	}
	return context;
};
